import { supabase } from "@/lib/supabase";
import { getCurrentOrganizationId } from "@/helpers/auth.helpers";
import type { BuildReviewAction, BuildReviewEvent, RevisionReason } from "@/data/buildReviewTypes";
import type {
  AssignmentType,
  CloseoutStatus,
  Contractor,
  ContractorOpportunity,
  ContractorResponseStatus,
  DispatchStatus,
  Issue,
  IssueSeverity,
  IssueSource,
  IssueStatus,
  IssueType,
  Job,
  JobFinancials,
  JobStage,
  MaterialReadiness,
  PermitStatus,
  ProjectType,
  CustomerCredit,
} from "@/data/types";

export interface ReportsQuote {
  id: string;
  createdBy: string;
  status: string;
  totalEstimate: number;
  leadSource: string;
}

export interface ReportsDataBundle {
  jobs: Job[];
  issues: Issue[];
  contractors: Contractor[];
  opportunities: ContractorOpportunity[];
  credits: CustomerCredit[];
  buildReviewEvents: BuildReviewEvent[];
  quotes: ReportsQuote[];
}

export interface ReportsDataResponse {
  data: ReportsDataBundle;
  error: unknown | null;
}

type JsonRecord = Record<string, unknown>;

type ReportsJobRow = {
  job_id?: string | null;
  job_number?: string | null;
  customer_name?: string | null;
  customer_phone?: string | null;
  customer_email?: string | null;
  address_line1?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  branch?: string | null;
  region?: string | null;
  current_stage?: string | null;
  project_type?: string | null;
  material_readiness?: string | null;
  permit_required?: boolean | null;
  permit_status?: string | null;
  dispatch_status?: string | null;
  assignment_type?: string | null;
  assigned_crew?: string | null;
  assigned_subcontractor?: string | null;
  assigned_subcontractor_id?: string | null;
  scheduled_install_date?: string | null;
  target_install_date?: string | null;
  qa_submitted?: boolean | null;
  approved_for_ordering?: boolean | null;
  build_review_approved?: boolean | null;
  build_review_status?: string | null;
  hold_status?: string | null;
  hold_reason?: string | null;
  planning_status?: string | null;
  closeout_status?: string | null;
  payout_amount?: number | string | null;
  financials?: JsonRecord | null;
  marketing?: JsonRecord | null;
  bound_job_spec?: Job["boundJobSpec"] | null;
};

type ReportsIssueRow = {
  issue_id?: string | null;
  job_id?: string | null;
  issue_type?: string | null;
  description?: string | null;
  status?: string | null;
  severity?: string | null;
  owner?: string | null;
  source?: string | null;
  photo?: string | null;
  related_contractor_id?: string | null;
  resolution_notes?: string | null;
  date_created?: string | null;
  date_resolved?: string | null;
};

type ReportsContractorRow = {
  contractor_id?: string | null;
  company_name?: string | null;
  contact_name?: string | null;
  phone?: string | null;
  email?: string | null;
  active?: boolean | null;
  rating?: number | string | null;
  branch?: string | null;
  service_regions?: unknown;
  tags?: unknown;
  joined_date?: string | null;
};

type ReportsContractorOpportunityRow = {
  opportunity_id?: string | null;
  job_id?: string | null;
  contractor_id?: string | null;
  sent_date?: string | null;
  response_date?: string | null;
  response_status?: string | null;
  bid_amount?: number | string | null;
  bid_notes?: string | null;
  is_winner?: boolean | null;
};

type ReportsQuoteRow = {
  quote_id?: string | null;
  created_by?: string | null;
  status?: string | null;
  total_estimate?: number | string | null;
  lead_source?: string | null;
};

type ReportsCreditRow = {
  credit_id?: string | null;
  customer_id?: string | null;
  job_id?: string | null;
  issue_id?: string | null;
  related_contractor_id?: string | null;
  related_crew_name?: string | null;
  credit_amount?: number | string | null;
  credit_type?: string | null;
  reason_category?: string | null;
  attribution_type?: string | null;
  notes?: string | null;
  status?: string | null;
  date_created?: string | null;
  created_by?: string | null;
};

type ReportsBuildReviewEventRow = {
  event_id?: string | null;
  job_id?: string | null;
  action?: string | null;
  event_at?: string | null;
  performed_by?: string | null;
  notes?: string | null;
  revision_notes?: string | null;
  revision_reason?: string | null;
  material_changes?: string | null;
};

const PROJECT_TYPES = new Set<ProjectType>([
  "Shower Remodel",
  "Shower Remodel (Wet Space Only)",
  "Bathtub Remodel",
  "Bathtub Remodel (Wet Space Only)",
  "Tub to Shower Conversion",
  "Tub to Shower Conversion (Wet Space Only)",
  "Full Bathroom Remodel",
  "Walk-in Tub Installation",
  "Window Install",
  "Door Install",
  "Flooring",
  "Roofing",
  "Painting",
  "Plumbing",
  "Drywall",
  "Other",
]);

function text(value: unknown, fallback = ""): string {
  if (value == null) return fallback;
  const normalized = String(value);
  return normalized.length > 0 ? normalized : fallback;
}

function num(value: unknown): number {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
}

function asArray(value: unknown): string[] {
  if (Array.isArray(value)) return value.map((v) => text(v)).filter(Boolean);
  return [];
}

function asRecord(value: unknown): JsonRecord {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as JsonRecord)
    : {};
}

function normalizeProjectType(value: unknown): ProjectType {
  const candidate = text(value, "Other") as ProjectType;
  return PROJECT_TYPES.has(candidate) ? candidate : "Other";
}

function financialsFromRow(row: ReportsJobRow): JobFinancials {
  const raw = asRecord(row.financials);
  const contractValue = num(raw.contractValue ?? raw.contract_value ?? raw.contractAmount ?? raw.contract_amount ?? raw.total ?? raw.totalEstimate);
  const estimatedMaterialCost = num(raw.estimatedMaterialCost ?? raw.estimated_material_cost);
  const estimatedLaborCost = num(raw.estimatedLaborCost ?? raw.estimated_labor_cost);
  const subcontractorPayout = num(raw.subcontractorPayout ?? raw.subcontractor_payout);
  const marginEstimate = num(raw.marginEstimate ?? raw.margin_estimate ?? (contractValue - estimatedMaterialCost - estimatedLaborCost - subcontractorPayout));
  return {
    contractValue,
    estimatedMaterialCost,
    estimatedLaborCost,
    subcontractorPayout,
    marginEstimate,
    paymentStatus: text(raw.paymentStatus ?? raw.payment_status, "Not Invoiced") as JobFinancials["paymentStatus"],
    payoutStatus: text(raw.payoutStatus ?? raw.payout_status, "Not Submitted") as JobFinancials["payoutStatus"],
  };
}

function marketingFromRow(row: ReportsJobRow): Job["marketing"] {
  const raw = asRecord(row.marketing);
  return {
    leadSource: text(raw.leadSource ?? raw.lead_source, "Unknown"),
    campaignName: text(raw.campaignName ?? raw.campaign_name),
    aggregatorPlatform: text(raw.aggregatorPlatform ?? raw.aggregator_platform),
    callCenterRep: text(raw.callCenterRep ?? raw.call_center_rep),
    appointmentDate: text(raw.appointmentDate ?? raw.appointment_date),
    saleOutcome: text(raw.saleOutcome ?? raw.sale_outcome),
    leadCost: num(raw.leadCost ?? raw.lead_cost),
    campaign: text(raw.campaign),
  };
}

function mapJob(row: ReportsJobRow): Job {
  const jobId = text(row.job_id);
  return {
    id: jobId,
    jobNumber: text(row.job_number),
    customerName: text(row.customer_name, "Unknown"),
    address: {
      street: text(row.address_line1),
      city: text(row.city),
      state: text(row.state),
      zip: text(row.postal_code),
    },
    phone: text(row.customer_phone),
    email: text(row.customer_email),
    secondaryContactName: "",
    secondaryContactPhone: "",
    secondaryContactEmail: "",
    projectType: normalizeProjectType(row.project_type),
    assignedCrew: text(row.assigned_crew),
    assignmentType: text(row.assignment_type, "Unassigned") as AssignmentType,
    scheduledInstallDate: text(row.scheduled_install_date),
    permitRequired: row.permit_required === true,
    permitStatus: text(row.permit_status, "Not Required") as PermitStatus,
    orderStatus: "Not Ordered",
    materialsStatus: "Not Ready",
    dispatchStatus: text(row.dispatch_status, "Not Ready") as DispatchStatus,
    contractorAssignmentStatus: "",
    currentStage: text(row.current_stage, "Sale Closed") as JobStage,
    notes: "",
    issueFlag: false,
    scopeSummary: "",
    payoutAmount: num(row.payout_amount),
    assignedSubcontractor: text(row.assigned_subcontractor),
    assignedSubcontractorId: text(row.assigned_subcontractor_id) || undefined,
    targetInstallDate: text(row.target_install_date),
    activityHistory: [],
    photos: [],
    qaSubmitted: row.qa_submitted === true,
    materialReadiness: text(row.material_readiness, "Not Ordered") as MaterialReadiness,
    materialReadinessNotes: "",
    missingItemsList: "",
    procurementStatus: "Not Ordered",
    warehouseStatus: "Not Received",
    branch: text(row.branch),
    region: text(row.region),
    salesRep: "",
    closeoutStatus: text(row.closeout_status, "Not Started") as CloseoutStatus,
    finalDocumentationComplete: false,
    customerSignoff: false,
    closeoutNotes: "",
    financials: financialsFromRow(row),
    marketing: marketingFromRow(row),
    rescissionStatus: "Not Applicable",
    approvedForOrdering: row.approved_for_ordering === true,
    buildReviewApproved: row.build_review_approved === true,
    buildReviewStatus: text(row.build_review_status) as Job["buildReviewStatus"],
    holdStatus: text(row.hold_status) || undefined,
    holdReason: text(row.hold_reason) || undefined,
    planningStatus: text(row.planning_status) as Job["planningStatus"],
    boundJobSpec: row.bound_job_spec ?? undefined,
  } as Job;
}

function mapIssue(row: ReportsIssueRow): Issue {
  return {
    id: text(row.issue_id),
    jobId: text(row.job_id),
    issueType: text(row.issue_type, "Other") as IssueType,
    severity: text(row.severity, "Medium") as IssueSeverity,
    description: text(row.description),
    photo: text(row.photo),
    owner: text(row.owner),
    status: text(row.status, "Open") as IssueStatus,
    dateCreated: text(row.date_created, new Date().toISOString()),
    source: text(row.source, "Admin") as IssueSource,
    relatedContractorId: text(row.related_contractor_id) || undefined,
    resolutionNotes: text(row.resolution_notes),
    dateResolved: text(row.date_resolved) || undefined,
  };
}

function mapContractor(row: ReportsContractorRow): Contractor {
  return {
    id: text(row.contractor_id),
    companyName: text(row.company_name, "Unknown Contractor"),
    contactName: text(row.contact_name),
    phone: text(row.phone),
    email: text(row.email),
    serviceArea: "",
    serviceRegions: asArray(row.service_regions),
    tradeType: "",
    active: row.active !== false,
    rating: num(row.rating),
    responsivenessRating: 0,
    notes: "",
    insuranceOnFile: false,
    insuranceExpiry: "",
    w9OnFile: false,
    licenseNumber: "",
    complianceNotes: "",
    onboardingStatus: "Approved",
    agreementSigned: false,
    tags: asArray(row.tags),
    branch: text(row.branch),
    joinedDate: text(row.joined_date),
    availabilityStatus: "Available",
    maxWeeklyJobs: 0,
    serviceZipCodes: [],
  };
}

function mapOpportunity(row: ReportsContractorOpportunityRow): ContractorOpportunity {
  return {
    id: text(row.opportunity_id),
    jobId: text(row.job_id),
    contractorId: text(row.contractor_id),
    sentDate: text(row.sent_date),
    viewedDate: "",
    responseDate: text(row.response_date),
    responseStatus: text(row.response_status, "Pending") as ContractorResponseStatus,
    bidAmount: row.bid_amount == null ? null : num(row.bid_amount),
    bidNotes: text(row.bid_notes),
    isWinner: row.is_winner === true,
  };
}

function mapQuote(row: ReportsQuoteRow): ReportsQuote {
  return {
    id: text(row.quote_id),
    createdBy: text(row.created_by, "Unknown"),
    status: text(row.status, "Draft"),
    totalEstimate: num(row.total_estimate),
    leadSource: text(row.lead_source, "Direct / Unknown"),
  };
}

function mapCredit(row: ReportsCreditRow): CustomerCredit {
  return {
    id: text(row.credit_id),
    customerId: text(row.customer_id),
    jobId: text(row.job_id),
    issueId: text(row.issue_id) || undefined,
    relatedContractorId: text(row.related_contractor_id) || undefined,
    relatedCrewName: text(row.related_crew_name) || undefined,
    creditAmount: num(row.credit_amount),
    creditType: text(row.credit_type, "Other") as CustomerCredit["creditType"],
    reasonCategory: text(row.reason_category, "Other") as CustomerCredit["reasonCategory"],
    attributionType: text(row.attribution_type, "Unknown") as CustomerCredit["attributionType"],
    notes: text(row.notes),
    status: text(row.status, "Draft") as CustomerCredit["status"],
    dateCreated: text(row.date_created, new Date().toISOString()),
    createdBy: text(row.created_by),
  };
}

function mapBuildReviewEvent(row: ReportsBuildReviewEventRow): BuildReviewEvent {
  return {
    id: text(row.event_id),
    jobId: text(row.job_id),
    action: text(row.action, "submitted_for_review") as BuildReviewAction,
    timestamp: text(row.event_at, new Date().toISOString()),
    performedBy: text(row.performed_by),
    notes: text(row.notes) || undefined,
    revisionNotes: text(row.revision_notes) || undefined,
    revisionReason: (text(row.revision_reason) || undefined) as RevisionReason | undefined,
    materialChanges: text(row.material_changes) || undefined,
  };
}

const emptyBundle = (): ReportsDataBundle => ({
  jobs: [],
  issues: [],
  contractors: [],
  opportunities: [],
  credits: [],
  buildReviewEvents: [],
  quotes: [],
});

function firstError(results: Array<{ error: unknown | null }>): unknown | null {
  return results.find((result) => result.error)?.error ?? null;
}

export const ReportsPageService = {
  async getBundle(): Promise<ReportsDataResponse> {
    let organizationId: string;
    try {
      organizationId = await getCurrentOrganizationId();
    } catch (error) {
      console.warn("ReportsPageService.getBundle could not resolve organization:", error);
      return { data: emptyBundle(), error };
    }

    const [
      jobsRes,
      issuesRes,
      contractorsRes,
      opportunitiesRes,
      creditsRes,
      buildReviewRes,
      quotesRes,
    ] = await Promise.all([
      supabase
        .from("v_reports_jobs_ui")
        .select("*")
        .eq("organization_id", organizationId)
        .order("sort_at", { ascending: false })
        .limit(5000),
      supabase
        .from("v_reports_issues_ui")
        .select("*")
        .eq("organization_id", organizationId)
        .order("sort_at", { ascending: false })
        .limit(5000),
      supabase
        .from("v_reports_contractors_ui")
        .select("*")
        .eq("organization_id", organizationId)
        .order("company_name", { ascending: true })
        .limit(5000),
      supabase
        .from("v_reports_contractor_opportunities_ui")
        .select("*")
        .eq("organization_id", organizationId)
        .order("sent_date", { ascending: false })
        .limit(5000),
      supabase
        .from("v_reports_credits_ui")
        .select("*")
        .eq("organization_id", organizationId)
        .order("sort_at", { ascending: false })
        .limit(5000),
      supabase
        .from("v_reports_build_review_events_ui")
        .select("*")
        .eq("organization_id", organizationId)
        .order("event_at", { ascending: true })
        .limit(5000),
      supabase
        .from("v_reports_quotes_ui")
        .select("*")
        .eq("organization_id", organizationId)
        .order("sort_at", { ascending: false })
        .limit(5000),
    ]);

    const error = firstError([jobsRes, issuesRes, contractorsRes, opportunitiesRes, creditsRes, buildReviewRes, quotesRes]);
    if (error) {
      return { data: emptyBundle(), error };
    }

    return {
      data: {
        jobs: ((jobsRes.data ?? []) as ReportsJobRow[]).map(mapJob),
        issues: ((issuesRes.data ?? []) as ReportsIssueRow[]).map(mapIssue),
        contractors: ((contractorsRes.data ?? []) as ReportsContractorRow[]).map(mapContractor),
        opportunities: ((opportunitiesRes.data ?? []) as ReportsContractorOpportunityRow[]).map(mapOpportunity),
        credits: ((creditsRes.data ?? []) as ReportsCreditRow[]).map(mapCredit),
        buildReviewEvents: ((buildReviewRes.data ?? []) as ReportsBuildReviewEventRow[]).map(mapBuildReviewEvent),
        quotes: ((quotesRes.data ?? []) as ReportsQuoteRow[]).map(mapQuote),
      },
      error: null,
    };
  },
};

export { emptyBundle as createEmptyReportsBundle };
